import React from 'react';

import {
  Document,
  Font,
  Image,
  Page,
  Text,
  View,
  StyleSheet as PdfStyleSheet,
} from '@react-pdf/renderer';

import logo from '../../../assets/logo.png';
import {
  POR_UNIDADE,
  POR_PESO_FIXO,
  POR_PESO_VARIAVEL_SEM_UNIDADE,
  formatDateDDMMYYYY,
} from '../../../utils';

Font.register({
  family: 'Roboto',
  fonts: [
    {
      src:
        'https://raw.githubusercontent.com/googlefonts/roboto/main/src/hinted/Roboto-Regular.ttf',
      fontWeight: 400,
    },
    {
      src:
        'https://raw.githubusercontent.com/googlefonts/roboto/main/src/hinted/Roboto-Black.ttf',
      fontWeight: 900,
    },
  ],
});

const NOMES_ANDAR = ['Térreo', '1º Aéreo', '2º Aéreo'];

export function getNomeAndar(andar) {
  return NOMES_ANDAR[andar - 1] || `Andar ${andar}`;
}

export function formatarValidadeParaPDF(date) {
  return formatDateDDMMYYYY(date);
}

// Mesma regra de identificação do fornecedor usada no backend
// (Utils/identificacaoFornecedor.js): nome fantasia, senão razão social,
// senão o nome.
export function getIdentificacaoFornecedor(fornecedor) {
  if (!fornecedor) return '';
  return (
    fornecedor.nome_fantasia || fornecedor.razao_social || fornecedor.nome || ''
  );
}

function normalizarParaArquivo(texto) {
  const CODIGO_INICIO_DIACRITICOS = 768; // 0x0300
  const CODIGO_FIM_DIACRITICOS = 879; // 0x036f

  const semAcentos = (texto || '')
    .normalize('NFD')
    .split('')
    .filter((caractere) => {
      const codigo = caractere.charCodeAt(0);
      return codigo < CODIGO_INICIO_DIACRITICOS || codigo > CODIGO_FIM_DIACRITICOS;
    })
    .join('');

  return semAcentos
    .replace(/[^a-zA-Z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .toLowerCase();
}

function removerZerosEsquerda(valor) {
  return String(valor || '').replace(/^0+(?=\d)/, '');
}

export function getNomeArquivoPdfPallets(
  fornecedor,
  numeroNotaFiscal,
  serieNotaFiscal,
) {
  const descricaoNormalizada = normalizarParaArquivo(
    getIdentificacaoFornecedor(fornecedor),
  );
  const numero = removerZerosEsquerda(numeroNotaFiscal);
  const serie = removerZerosEsquerda(serieNotaFiscal);
  return `pallets_validades_fornecedor_${descricaoNormalizada}_serie_${serie}_numero_${numero}.pdf`;
}

// Mesmo formato (DD/MM/AA, ano com 2 dígitos) usado no botão "Gerar PDF de
// Validade" da aba Validades — mantido separado de formatarValidadeParaPDF
// (que usa ano com 4 dígitos) pra reproduzir esse PDF exatamente.
export function formatarDataValidadeCurta(validade) {
  const arr = validade ? validade.split('-') : [];
  return arr.length === 3
    ? `${arr[2]}/${arr[1]}/${String(arr[0]).slice(2)}`
    : '-';
}

export function getQuantidadeCompradaLote(lote) {
  const tipoUnidade =
    lote.produto && lote.produto.unidade ? lote.produto.unidade.tipo : '';
  if (tipoUnidade === POR_UNIDADE || tipoUnidade === POR_PESO_FIXO) {
    return lote.unidades;
  }
  return lote.peso;
}

export function getQuantidadeCaixasPorPallet(
  produto,
  quantidadeComprada,
  indexPallet,
  numeroPallets,
) {
  if (!produto) return 0;

  const caixasPorPalletNominal = produto.is_caixa_nivel_2_pallet
    ? produto.unidades_caixa_nivel_2
    : produto.unidades_caixa_nivel_2 * produto.unidades_caixa_nivel_3;
  if (!caixasPorPalletNominal) return 0;

  const tipoUnidade = produto.unidade ? produto.unidade.tipo : '';

  let totalCaixasNivel1 = 0;
  if (tipoUnidade === POR_UNIDADE || tipoUnidade === POR_PESO_FIXO) {
    if (!produto.unidades_caixa_nivel_1) return 0;
    totalCaixasNivel1 = quantidadeComprada / produto.unidades_caixa_nivel_1;
  } else if (tipoUnidade === POR_PESO_VARIAVEL_SEM_UNIDADE) {
    if (!produto.peso_caixa_nivel_1) return 0;
    totalCaixasNivel1 = quantidadeComprada / produto.peso_caixa_nivel_1;
  } else {
    return 0;
  }

  // A carga é dividida igualmente entre os pallets — não preenche o
  // primeiro até a capacidade nominal e joga o resto no último, senão um
  // pallet manual a mais não ajuda a reduzir o peso de nenhum deles. O
  // resto da divisão inteira é espalhado nos primeiros pallets (1 caixa a
  // mais cada) em vez de concentrado todo no último, pra não recriar o
  // mesmo desequilíbrio em menor escala.
  const total = numeroPallets && numeroPallets > 0 ? numeroPallets : 1;
  const totalCaixasArredondado = Math.round(totalCaixasNivel1);
  const caixasPorPalletBase = Math.floor(totalCaixasArredondado / total);
  const resto = totalCaixasArredondado - caixasPorPalletBase * total;
  const caixas =
    indexPallet < resto ? caixasPorPalletBase + 1 : caixasPorPalletBase;
  return Math.max(caixas, 0);
}

export function getPesoDoPallet(
  produto,
  quantidadeComprada,
  indexPallet,
  numeroPallets,
) {
  if (!produto) return null;
  const tipoUnidade = produto.unidade ? produto.unidade.tipo : '';

  const caixasDoPallet = getQuantidadeCaixasPorPallet(
    produto,
    quantidadeComprada,
    indexPallet,
    numeroPallets,
  );

  if (tipoUnidade === POR_UNIDADE) {
    if (!produto.peso_padrao_na_compra) return null;
    return (
      caixasDoPallet *
      produto.unidades_caixa_nivel_1 *
      produto.peso_padrao_na_compra
    );
  }
  if (tipoUnidade === POR_PESO_FIXO) {
    if (!produto.peso_medio) return null;
    return caixasDoPallet * produto.unidades_caixa_nivel_1 * produto.peso_medio;
  }
  if (tipoUnidade === POR_PESO_VARIAVEL_SEM_UNIDADE) {
    return caixasDoPallet * produto.peso_caixa_nivel_1;
  }
  return null;
}

export function buildPalletsParaPDF(lotes) {
  const todosPallets = [];
  for (let i = 0; i < lotes.length; i += 1) {
    const lote = lotes[i];
    if (!lote.pallets || lote.pallets.length === 0) {
      // Item sem pallets: se o produto não é invencível (ou seja, tem
      // controle de validade), cada validade do item vira uma página no
      // mesmo formato do botão "Gerar PDF de Validade" da aba Validades
      // (nome do produto + data em destaque, sem endereço/palletização).
      if (lote.produto && !lote.produto.is_invencivel) {
        const nomeProduto = lote.produto.nome;
        const validades = lote.validades || [];
        if (validades.length === 0) {
          todosPallets.push({
            tipoPagina: 'validade',
            nomeProduto,
            dataFormatada: '-',
          });
        } else {
          validades.forEach((validade) => {
            todosPallets.push({
              tipoPagina: 'validade',
              nomeProduto,
              dataFormatada: formatarDataValidadeCurta(validade),
            });
          });
        }
      }
      continue;
    }
    const quantidadeComprada =
      lote.quantidadeComprada !== undefined
        ? lote.quantidadeComprada
        : getQuantidadeCompradaLote(lote);
    lote.pallets.forEach((row, ind) => {
      todosPallets.push({
        nomeProduto: lote.produto.descricao_no_cartaz || lote.produto.nome,
        codigo: row.codigo,
        andar: row.andar,
        nomeAndar: getNomeAndar(row.andar),
        quantidadeCaixas: getQuantidadeCaixasPorPallet(
          lote.produto,
          quantidadeComprada,
          ind,
          lote.pallets.length,
        ),
        validades: (row.validades || []).map((validade) =>
          formatarValidadeParaPDF(validade),
        ),
      });
    });
  }
  return todosPallets;
}

const pdfPalletStyles = PdfStyleSheet.create({
  page: {
    padding: 20,
  },
  moldura: {
    flex: 1,
    flexDirection: 'column',
    height: '100%',
    padding: 28,
    border: '2mm solid black',
  },
  tituloContainer: {
    marginBottom: 10,
  },
  titulo: {
    fontFamily: 'Roboto',
    fontSize: 64,
    fontWeight: 900,
    textAlign: 'center',
    textTransform: 'uppercase',
  },
  enderecoAndar: {
    fontFamily: 'Roboto',
    fontSize: 44,
    fontWeight: 900,
    textAlign: 'center',
    marginTop: 24,
    marginBottom: 12,
  },
  palletizacao: {
    fontFamily: 'Roboto',
    fontSize: 44,
    fontWeight: 900,
    textAlign: 'center',
    marginBottom: 8,
  },
  validadeItem: {
    fontFamily: 'Roboto',
    fontSize: 44,
    fontWeight: 900,
    textAlign: 'center',
    lineHeight: 1.1,
    marginTop: 8,
  },
  logo: {
    width: 100,
    height: 'auto',
    position: 'absolute',
    bottom: 20,
    right: 20,
  },
});

// Mesmo layout do botão "Gerar PDF de Validade" da aba Validades: só nome do
// produto + data em destaque, sem moldura/endereço/palletização/logo.
const validadePdfStyles = PdfStyleSheet.create({
  page: {
    flexDirection: 'column',
    alignItems: 'stretch',
    justifyContent: 'center',
    padding: 20,
  },
  view: {
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
  },
  nome: {
    fontFamily: 'Roboto',
    fontSize: 60,
    fontWeight: 900,
    textAlign: 'center',
    textTransform: 'uppercase',
    marginBottom: 30,
    width: '100%',
  },
  data: {
    fontSize: 160,
    fontWeight: 'bold',
    textAlign: 'center',
    width: '100%',
  },
});

export const PaginaValidade = ({ nomeProduto, dataFormatada }) => (
  <Page size="A4" orientation="landscape" style={validadePdfStyles.page}>
    <View style={validadePdfStyles.view}>
      <Text style={validadePdfStyles.nome}>{nomeProduto}</Text>
      <Text style={validadePdfStyles.data}>{dataFormatada}</Text>
    </View>
  </Page>
);

export const ValidadePDF = ({ nomeProduto, dataFormatada }) => (
  <Document>
    <PaginaValidade nomeProduto={nomeProduto} dataFormatada={dataFormatada} />
  </Document>
);

export const PalletPDF = ({ pallets }) => (
  <Document>
    {pallets.map((pallet, index) =>
      pallet.tipoPagina === 'validade' ? (
        <PaginaValidade
          key={`validade-pdf-${index}`}
          nomeProduto={pallet.nomeProduto}
          dataFormatada={pallet.dataFormatada}
        />
      ) : (
        <Page
          key={`pallet-pdf-${index}`}
          size="A4"
          orientation="landscape"
          style={pdfPalletStyles.page}
          wrap={false}
        >
          <View style={pdfPalletStyles.moldura}>
            <View style={pdfPalletStyles.tituloContainer}>
              {(pallet.nomeProduto || '').split('\n').map((linha, indLinha) => (
                <Text
                  key={`titulo-linha-${indLinha}`}
                  style={pdfPalletStyles.titulo}
                >
                  {linha}
                </Text>
              ))}
            </View>
            <Text style={pdfPalletStyles.enderecoAndar}>
              {pallet.codigo || '-'}
              {pallet.andar ? ` - ${pallet.nomeAndar}` : ''}
            </Text>
            <Text style={pdfPalletStyles.palletizacao}>
              {`Palletização - ${pallet.quantidadeCaixas} caixas`}
            </Text>
            <Text style={pdfPalletStyles.validadeItem}>
              {pallet.validades && pallet.validades.length > 0
                ? pallet.validades.join(', ')
                : '-'}
            </Text>
            <Image style={pdfPalletStyles.logo} src={logo} />
          </View>
        </Page>
      ),
    )}
  </Document>
);
